/**
 * `AW5` · Fila desplegable de la tabla «Conversaciones».
 *
 * La celda `contenido` muestra el texto plegado a un máximo de presentación y
 * permite desplegarlo in-place para ver el mensaje completo junto con las
 * fuentes consultadas.
 *
 * `guard` ① · Esta fila NO pega contra el servidor al desplegar. No hay
 * llamada de detalle, no hay `fetch` perezoso y no se agrega ningún endpoint
 * nuevo: el `item` que recibe ya trae `fila.contenido` completo y
 * `item.fuentes` listas para mostrar.
 *
 * `guard` ② · Lo que la fila plegada muestra es un recorte de PRESENTACIÓN
 * (`slice`) sobre el texto completo que ya posee. La pantalla nunca pide una
 * versión corta del contenido al borde.
 *
 * Regla de fuentes (`CC3`):
 * - `item.fuentes === null`  → mensaje del usuario → NO se muestra la sección.
 * - `item.fuentes === []`    → chatbot que no consultó fuentes → sección vacía.
 * - `item.fuentes.length > 0` → chatbot con fuentes → sección con la lista.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ LO QUE `R3-UI1` CAMBIA ACÁ, Y POR QUÉ CADA COSA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **Los tres identificadores pasan por la primitiva de `R3-UI0`**
 *    (`src/identificador-acortado.tsx`). `PRD_R3.md:717` pide «ca01…» con su
 *    burbuja y «Copiar», y dice que es *«el mismo comportamiento que tiene el ID
 *    de Participantes (R3-F5)»*; `:1973` lo dice al revés. **Dos frases que se
 *    señalan mutuamente no se pueden cumplir con dos copias**: un `slice(0, 4)`
 *    local acá sería la segunda, y el día que alguien ajuste el cierre con
 *    Escape o el texto del botón, ningún criterio de ninguna de las dos
 *    pantallas lo mide. Es el `rationale_hint` textual del guard B6.
 *
 * ② **El «+» tiene DOS mitades, unidas por `||`.** `PRD_R3.md:719`: *«Un mensaje
 *    del sistema que consultó al menos un documento también muestra “+”, **aunque
 *    sea corto**»*, y *«Un mensaje corto que no consultó documentos no tiene
 *    “+”»*. Lo que había era sólo la primera mitad (el largo), así que una
 *    respuesta corta con fuentes las escondía sin forma de verlas.
 *
 * ③ **Las fuentes van en UNA línea, separadas por « · », y el rótulo va sin dos
 *    puntos.** `PRD_R3.md:719`, textual: *«“Fuentes consultadas” seguido de los
 *    nombres de los documentos que consultó para responder, separados por “ · ”»*.
 *    El `<ul>` que había era una lista vertical.
 *
 * ④ **El `motivo` va DEBAJO de la etiqueta de Estado, en la misma celda.**
 *    `PRD_R3.md:713`: *«en “Fallido”, el motivo en palabras debajo de la
 *    etiqueta»*. ⛔ **No es una columna**: la tabla tiene DIEZ (`:700-713`) y la
 *    undécima (`motivo`) es sólo del CSV (regla 29).
 *
 * ⛔⛔ **Y LA PANTALLA NO TRADUCE NINGÚN CÓDIGO.** `fila.motivo` llega **ya
 * resuelto** —lo calcula el módulo al leer con `reasonFor` de
 * `contracts/channel`, que es el mismo traductor que usa Envíos—. Armar acá una
 * tabla código→motivo «porque es sólo presentación» es exactamente lo que
 * `R3-F1-CA-108` dice que no puede existir: dos tablas divergen en la primera
 * frase que alguien corrija, y la que corrija primero gana en una pantalla y
 * pierde en la otra. Por eso este archivo **no lee `fila.codigo_error`**.
 */

import { useState } from "react"
import type { JSX } from "react"
import type { MensajeListado } from "@contracts/conversation/index.ts"
import { IdentificadorAcortado } from "../identificador-acortado.tsx"
import {
  formatearEsSistema,
  formatearEstadoMensaje,
  formatearFecha,
  formatearFuentesConsultadas,
} from "./formato.ts"

/**
 * Umbral de truncado de presentación para la fila plegada.
 *
 * ⭐ `R3-UI1` · ~~«Esta longitud no sale de ninguna fuente del repo»~~ — **ya
 * sale**: `PRD_R3.md:719`, textual, *«Si el mensaje no entra en su columna (más
 * de 80 caracteres), se ve recortado con “…”»*. El número que estaba elegido a
 * ojo resultó ser el del PRD; queda con su fuente escrita para que nadie lo
 * vuelva a mover «porque es una decisión de la interfaz».
 */
const LONGITUD_MAXIMA_PLEGADA = 80

/** El separador de los nombres de documento. `PRD_R3.md:719`: espacio, U+00B7, espacio. */
const SEPARADOR_DE_FUENTES = " · "

export function FilaDesplegable({ item }: { readonly item: MensajeListado }): JSX.Element {
  const fila = item.fila
  const [desplegada, setDesplegada] = useState(false)

  const esLargo = fila.contenido.length > LONGITUD_MAXIMA_PLEGADA
  /*
   * ⭐ LAS DOS MITADES DEL «+», en UNA expresión. Ver ② del docblock.
   *
   * ⛔ La comparación es `> 0` y no `!== null`: con `!== null`, un mensaje del
   * sistema que consultó CERO documentos —que es un caso legítimo y distinto de
   * `null`, ⟦el-contador-y-la-lista-son-dos-cosas⟧— y además es corto se llevaría
   * un «+» que no le toca, y al abrirlo no habría nada que mostrar.
   */
  const tieneDesplegable =
    fila.contenido.length > LONGITUD_MAXIMA_PLEGADA ||
    (item.fuentes !== null && item.fuentes.length > 0)
  const mostrarCompleto = !esLargo || desplegada
  const textoVisible = mostrarCompleto
    ? fila.contenido
    : `${fila.contenido.slice(0, LONGITUD_MAXIMA_PLEGADA)}…`

  /*
   * Las fuentes se ven cuando la fila está desplegada. `mostrarCompleto` solo no
   * alcanza: un mensaje CORTO con fuentes lo tiene en `true` desde el arranque, y
   * `PRD_R3.md:719` dice que las fuentes aparecen al **tocar «+»**.
   */
  const mostrarFuentes = desplegada && item.fuentes !== null && item.fuentes.length > 0

  const estado = formatearEstadoMensaje(fila.estado_mensaje)

  return (
    <tr>
      {/*
        ⛔ El `que` no es decorativo: sin él, una fila con tres identificadores
        acortados le ofrece a un lector de pantalla tres botones llamados «ca01…»,
        «5e01…» y «9a01…», y ninguno dice de qué son. Los nombres son los rótulos
        de las columnas (`PRD_R3.md:700-702`).
      */}
      <td>
        <IdentificadorAcortado identificador={fila.id_conversacion} que="Conversación" />
      </td>
      <td>
        <IdentificadorAcortado identificador={fila.id_sesion} que="Sesión" />
      </td>
      <td>
        <IdentificadorAcortado identificador={fila.id_usuario} que="Participante" />
      </td>
      <td>{fila.telefono}</td>
      <td className="conversaciones__contenido">
        {/*
          El texto del mensaje va en su PROPIO nodo y no suelto en la celda.
          La celda `contenido` no es sólo el mensaje —`AW4` la describe como «el
          mensaje. Si es muy largo, un `+` en la fila lo despliega completo. En
          los mensajes del chatbot, la vista muestra al final las fuentes
          consultadas»—, así que el `textContent` de la celda es la suma de las
          tres cosas y NO es el valor de la columna. Este nodo es el valor, y es
          lo que un examen tiene que mirar para afirmar qué mensaje trae la fila.
        */}
        <span className="conversaciones__texto">{textoVisible}</span>
        {tieneDesplegable && (
          <button
            type="button"
            className="boton boton--chico conversaciones__desplegar"
            aria-expanded={desplegada}
            aria-label={desplegada ? "Plegar mensaje" : "Desplegar mensaje"}
            onClick={() => setDesplegada(!desplegada)}
          >
            {desplegada ? "−" : "+"}
          </button>
        )}
        {mostrarFuentes && item.fuentes !== null && (
          <div className="conversaciones__fuentes">
            {/*
              ⛔ Sin dos puntos y en la misma línea que los nombres: el PRD
              escribe «Fuentes consultadas» **seguido de** los nombres. El
              `<span>` conserva el rótulo como nodo propio para que un examen
              pueda distinguir el rótulo de los nombres.
            */}
            <span className="conversaciones__fuentes-titulo">Fuentes consultadas</span>{" "}
            <span className="conversaciones__fuentes-nombres">
              {item.fuentes.join(SEPARADOR_DE_FUENTES)}
            </span>
          </div>
        )}
      </td>
      <td>{formatearEsSistema(fila.es_sistema)}</td>
      <td>{formatearFecha(fila.fecha_hora)}</td>
      <td className="conversaciones__numero">
        {formatearFuentesConsultadas(fila.fuentes_consultadas)}
      </td>
      <td>{fila.posicion_en_sesion}</td>
      {/*
        ⭐ La celda de Estado lleva DOS cosas y sigue siendo UNA columna: la
        etiqueta, y debajo el motivo cuando lo hay. Ver ④ del docblock.
      */}
      <td>
        <span className="conversaciones__estado">{estado}</span>
        {fila.motivo !== null && (
          <span className="conversaciones__motivo">{fila.motivo}</span>
        )}
      </td>
    </tr>
  )
}
